import { createSlice } from "@reduxjs/toolkit";
import en from "../../../en.json";
import esUserReview from "../../locale/es/userReview.json";

export const DEFAULT_LANGUAGE = "en";

/**
 * Partial override bundles for the `useLang` string system (root `en.json`).
 *
 * These are NOT react-i18next namespaces — that is a separate system living in
 * `src/i18n.js` + `src/locale/{en,fr}/`. Bundles here are merged OVER the full
 * English set, so any key a bundle does not translate falls back to English
 * instead of rendering `undefined`. `useLang` has no fallback chain of its own,
 * which is why the merge (rather than a straight swap) matters.
 */
const OVERRIDES = {
  es: esUserReview,
};

export const SUPPORTED_LANGUAGES = [DEFAULT_LANGUAGE, ...Object.keys(OVERRIDES)];

/**
 * Normalise a lead/user language code to a bundle key.
 * Handles region suffixes and casing ("es-MX", "es_ES", "ES" -> "es") and falls
 * back to English for anything we do not ship a bundle for.
 */
export const normalizeLanguageCode = code => {
  const base = String(code || "")
    .trim()
    .toLowerCase()
    .split(/[-_]/)[0];
  return OVERRIDES[base] ? base : DEFAULT_LANGUAGE;
};

const buildStatement = code => ({ ...en, ...(OVERRIDES[code] || {}) });

const initialState = {
  statement: buildStatement(DEFAULT_LANGUAGE),
  language: DEFAULT_LANGUAGE,
};

export const languageSlice = createSlice({
  name: "language",
  initialState,
  reducers: {
    setLanguage: (state, action) => {
      const code = normalizeLanguageCode(action.payload);
      if (code === state.language) return;
      state.language = code;
      state.statement = buildStatement(code);
    },
    resetLanguage: state => {
      if (state.language === DEFAULT_LANGUAGE) return;
      state.language = DEFAULT_LANGUAGE;
      state.statement = buildStatement(DEFAULT_LANGUAGE);
    },
  },
});

export const { setLanguage, resetLanguage } = languageSlice.actions;

export const getStatements = state => state?.language?.statement;
export const getLanguage = state => state?.language?.language || DEFAULT_LANGUAGE;

export default languageSlice.reducer;
